/**
 * Poseidon dashboard — staleness evaluation.
 *
 * §13 Phase 1 acceptance gate: "dashboard accurately mirrors MT5 demo
 * account/position/order data and **labels stale data visibly**". Staleness is
 * therefore computed in one place and rendered by a single badge component, so
 * no page can quietly show a fresh-looking number that is actually minutes old.
 */
import { useEffect, useState } from 'react';

export type StalenessLevel = 'fresh' | 'aging' | 'stale' | 'unknown';
export type Tone = 'ok' | 'info' | 'warn' | 'danger' | 'muted';

export interface StalenessThresholds {
  aging: number;
  stale: number;
}

export interface Staleness {
  level: StalenessLevel;
  ageMs: number | null;
  label: string;
  tone: Tone;
  /** Operator instruction. Null when the data is fresh enough to act on. */
  advice: string | null;
}

export const STALENESS_TONE: Record<StalenessLevel, Tone> = {
  fresh: 'ok',
  aging: 'warn',
  stale: 'danger',
  unknown: 'muted',
};

export function computeStaleness(
  lastAt: string | null | undefined,
  thresholds: StalenessThresholds,
  now: number,
): Staleness {
  if (lastAt === null || lastAt === undefined || lastAt.length === 0) {
    return {
      level: 'unknown',
      ageMs: null,
      label: 'no data received',
      tone: 'muted',
      advice: 'The dashboard has not received any data from the daemon yet.',
    };
  }
  const parsed = new Date(lastAt).getTime();
  if (Number.isNaN(parsed)) {
    return {
      level: 'unknown',
      ageMs: null,
      label: 'unreadable timestamp',
      tone: 'muted',
      advice: 'The daemon sent a timestamp this build cannot parse.',
    };
  }
  // A timestamp in the future usually means clock skew between daemon and
  // browser. Treat it as suspect rather than as perfectly fresh.
  const ageMs = now - parsed;
  if (ageMs < 0) {
    return {
      level: 'stale',
      ageMs,
      label: 'timestamp is in the future',
      tone: 'danger',
      advice: 'Clock drift between the daemon and this browser. Verify the host clock before trusting these values.',
    };
  }
  if (ageMs >= thresholds.stale) {
    return {
      level: 'stale',
      ageMs,
      label: 'STALE',
      tone: 'danger',
      advice: 'Do not act on these values. Verify the MT5 bridge and daemon before enabling execution.',
    };
  }
  if (ageMs >= thresholds.aging) {
    return {
      level: 'aging',
      ageMs,
      label: 'aging',
      tone: 'warn',
      advice: 'Updates are slower than expected.',
    };
  }
  return { level: 'fresh', ageMs, label: 'live', tone: 'ok', advice: null };
}

/** A ticking clock. One interval per consumer is fine at this scale. */
export function useNow(intervalMs = 1_000): number {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return now;
}

export function useStaleness(
  lastAt: string | null | undefined,
  thresholds: StalenessThresholds,
  intervalMs = 1_000,
): Staleness {
  const now = useNow(intervalMs);
  return computeStaleness(lastAt, thresholds, now);
}

/** Newest of a set of timestamps; used to derive the dashboard-wide data clock. */
export function newestTimestamp(values: readonly (string | null | undefined)[]): string | null {
  let newest: number | null = null;
  let newestRaw: string | null = null;
  for (const value of values) {
    if (value === null || value === undefined || value.length === 0) continue;
    const parsed = new Date(value).getTime();
    if (Number.isNaN(parsed)) continue;
    if (newest === null || parsed > newest) {
      newest = parsed;
      newestRaw = value;
    }
  }
  return newestRaw;
}
